import { memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import { Button, Layout, PageTitle } from "lola-framework-ui-test";
import { AuraLayout } from "../../../components/AuraLayout";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { useStore } from "../../../store/globalState";
import { getEvents, safeCapture } from "../../../analytics";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";
import { SupportSheet } from "../../../components/SupportSheet";
import { fetchMaintenanceStatus, isDeadLinkError } from "../../../services/api";
import { useSessionStore } from "../../../store/useSessionStore";
import {
  maintenanceLinkId,
  readMaintenanceDetail,
} from "../../../hooks/useMaintenanceWatch";

const RELEASE_POLL_MS = 20_000;

export const MaintenancePage = memo(() => {
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const resetSession = useSessionStore((state) => state.resetSession);
  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;
  const initial = readMaintenanceDetail(location.state);
  const [copy, setCopy] = useState(initial);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    safeCapture(getEvents().SERVICE_UNAVAILABLE, {
      error_code: "SERVICE_UNAVAILABLE_MAINTENANCE",
    });
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const linkId = maintenanceLinkId();
      if (!linkId) return;
      try {
        const status = await fetchMaintenanceStatus(linkId, i18n.language);
        if (stopped) return;
        if (!status.active) {
          resetSession();
          navigate(`/${linkId}`, { replace: true });
          return;
        }
        setCopy(status);
      } catch (error) {
        if (isDeadLinkError(error)) return;
      }
    };
    const timer = window.setInterval(() => {
      void tick();
    }, RELEASE_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [i18n.language, navigate, resetSession]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={copy.title || t("error.maintenanceTitle")}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>
              {copy.body || t("error.maintenanceMessage")}
            </BodyCopy>
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => setSupportOpen(true)}
          >
            {t("error.contactSupport")}
          </Button>
        </section>
      </Layout.Footer>
      <SupportSheet
        visible={supportOpen}
        onClose={() => setSupportOpen(false)}
        channels={{
          email: copy.supportEmail,
          whatsapp: copy.supportWhatsappNumber,
        }}
      />
    </AuraLayout>
  );
});
